import React, { useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";
import * as actions from "../actions";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import AppHOC from "../../../generics/AppHOC";
import Spinner from "../../../common/Spinner";
import ErrorComponent from "../../../common/ErrorComponent";
import DashletTable from "../../../common/DashletTable";
import {
  isCustom,
  getReportTimePeriod,
  getLocalStorageFlag,
  downloadAsCSVFile,
  escapeCSVField
} from "../../../utils/common";
import { checkCookieValidity } from "../../../utils/waniCommon";
import { getThemeColors } from "../../../utils/colors";
import { formatBytes } from "../../../utils/format";
import timeFilterConfig from "../../../generics/config/time-filter-config";
import {
  columnsClientsName,
  getSourceClients
} from "./config/table/harborTableHeaderConfig";
import clientTableConfig from "./config/table/userBandwithTableConfig";
import { useI18n } from "../../../i18n/useI18n";
import css from "../overviewMagneticStyle.less";

const [HbrCard, HbrIconButton, HbrTooltip] = reactWrapper([
  "hbr-card",
  "hbr-icon-button",
  "hbr-tooltip"
]);

const DashletUserView = ({
  globalFilter,
  overview,
  actions,
  isReporting,
  reportData,
  isReportUserCallback
}) => {
  const { tViews } = useI18n('views');
  const {
    timePeriodSelected,
    timeFilter,
    currentTimeStamp,
    selectedSite,
    selectedCustomTime,
    selectedNhName
  } = globalFilter;
  const [currentPeriod, setCurrentPeriod] =
    useState('null');
  const [prevPeriod, setPrevPeriod] = useState(timeFilter.current_period[1]);
  const { userSummary, errorUser } = overview;
  const reportRange = reportData
    ? getReportTimePeriod(reportData.time_range)
    : timePeriodSelected;

  const updateDashletFilter = () => {
    const { setDashletFilter, setDashletLoading } = actions;
    setDashletLoading("USER");
    setDashletFilter("USER");
  };

  const updateView = () => {
    const { getTopUsers } = actions;
    getTopUsers(globalFilter.globalV4Payload);
  };

  useEffect(() => {
    if (!isCustom(timePeriodSelected)) updateDashletFilter();
  }, []);

  // callback function for executive summary report to identify data is present for top clients view
   useEffect(() => { 
     if(userSummary?.usage?.length !== undefined  && isReporting) {
       isReportUserCallback(true); 
     }
  }, [userSummary]);

  useEffect(() => {
    const currentOverlay = getLocalStorageFlag("currentOverlay");
    const isCrossLaunch = checkCookieValidity("cl-rec-apply-url");

    if (currentOverlay && (globalFilter.selectedOverlay === currentOverlay || isReporting || isCrossLaunch)) {
      const { setDashletLoading } = actions;
      if (!(isCustom(timePeriodSelected)) ||
        !(currentPeriod === timeFilter.current_period[0]) ||
        !(prevPeriod === timeFilter.current_period[1])
      ) {
        if (!(currentPeriod === timeFilter.current_period[0]))
          setCurrentPeriod(timeFilter.current_period[0]);
        if (!(prevPeriod === timeFilter.current_period[1]))
          setPrevPeriod(timeFilter.current_period[1])
        setDashletLoading("USER");
        updateView();
      } else if (
        (!(
          selectedCustomTime?.range?.start
          && selectedCustomTime?.range?.end
        ) && selectedSite > 0) || (isCustom(timePeriodSelected) && selectedSite && selectedCustomTime?.range)
      ) {
        setDashletLoading("USER");
        updateView();
      }
    }
  }, [
    timePeriodSelected,
    timeFilter.current_period[0],
    timeFilter.current_period[1],
    currentTimeStamp,
    selectedSite,
    selectedNhName
  ]);

  const getChartTooltipConfig = () => ({
    labels: [
      { key: "current", label: tViews('applicationOverviewLabelUsage') },
      {
        key: "previous",
        label: `${tViews('applicationOverviewLabelUsageIn')
          } ${timeFilterConfig[timePeriodSelected].toolTip}`
      }
    ],
    colors: getThemeColors(clientTableConfig.legend.theme),
    type: "bytes",
    format: "GB",
    decimal: 0,
    width: "300"
  });

  const formatTopUsedApps = apps => {
    if (!Array.isArray(apps)) return apps || "";

    return apps.join(" | ");
  };

  const handleCSVExport = () => {
    const tableColumns = clientTableConfig.columns;
    const chartTooltipConfig = getChartTooltipConfig();
    const currentUsageLabel = chartTooltipConfig.labels[0].label;
    const previousUsageLabel = chartTooltipConfig.labels[1].label;
    const headers = [
      tableColumns[0].title,
      tableColumns[1].title,
      tableColumns[2].title,
      tableColumns[3].title,
      `${tableColumns[3].title} ${tViews('trendingAppsChange')}`,
      `${tableColumns[4].title} - ${currentUsageLabel}`,
      `${tableColumns[4].title} - ${previousUsageLabel}`
    ];
    const rows = userSummary.usage.map(row => [
      row.site_name,
      row.source_ip,
      formatTopUsedApps(row.top_used_apps),
      formatBytes(row.usage),
      formatBytes(row.usage_change_byte),
      formatBytes(row.tooltip?.current ?? row.usage),
      formatBytes(row.tooltip?.previous)
    ]);
    const csvRows = [headers, ...rows].map(row =>
      row.map(escapeCSVField).join(",")
    );

    downloadAsCSVFile(
      csvRows.join("\n"),
      `${tViews('a11yLabels.overviewPageTitleLabel')}_${tViews('clientsTitle').toLowerCase()}`
    );
  };

  const showCsvExport = !isReporting &&
    !overview.userSummaryLoader &&
    !errorUser &&
    userSummary?.usage?.length > 0;

  return (
    <HbrCard
      className={css[isReporting ? "user-dashlet-reporting" : "user-dashlet"]}
      data-cy="user-dashlet"
      container={true}
    >
      <div className="user-dashlet-view" data-cy="user-view" data-testid="client-dashlet">
        {!isReporting && <div className="title-block">
          <div className="header-content hbr-type-h2" role="heading" aria-level={'2'}>
            {tViews('clientsTitle')}
          </div>
          {showCsvExport && (
            <div className="client-view-actions">
              <HbrTooltip content={tViews('downloadCSV')} hoist>
                <span>
                  <HbrIconButton
                    data-testid="overview-clients-csv-export"
                    size="20px"
                    name="file-csv"
                    onClick={handleCSVExport}
                    arialabel={tViews('downloadCSV')}
                  />
                </span>
              </HbrTooltip>
            </div>
          )}
        </div>}
        {overview.userSummaryLoader ? (
          <div
            className="spinner-component-clients-view"
            data-cy="spinner-componen-clients-view"
          >
            <Spinner />
          </div>
        ) : errorUser ? (
          <ErrorComponent
            {...errorUser}
            width="210px"
            className="small-dashlet-error"
          />
        ) : (
          <div className="content-block">
            {clientTableConfig && userSummary.usage && (
              <div className="table-view">
                <div className="title-clients-dashlet hbr-type-h3" role="heading" aria-level={'3'}>
                  {isReporting ? tViews('topClient') : tViews('topClients')}
                </div>
                <DashletTable
                  config={clientTableConfig}
                  data={userSummary.usage}
                  timeFilterType={
                    reportData ? reportRange : timePeriodSelected
                  }
                  chartTooltip={true}
                  chartConfig={getChartTooltipConfig()}
                  source={getSourceClients([])}
                  columnsName={columnsClientsName}
                  style={{ height: "40px", width: "530px" }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </HbrCard>
  );
};

DashletUserView.propTypes = {
  globalFilter: PropTypes.object.isRequired,
  overview: PropTypes.object,
  actions: PropTypes.object.isRequired,
  isReportUserCallback: PropTypes.func,
  isReporting: PropTypes.bool,
  reportData: PropTypes.object
};

const mapStateToProps = state => ({
  overview: state.vanalytics.overview
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(actions, dispatch)
});

export default (
  connect(mapStateToProps, mapDispatchToProps)(AppHOC(DashletUserView))
);
